import React, { useContext, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import styles from './aiChat.module.scss';
import {
  AIConversation,
  AIConversationMessage,
  AIConversationStatus,
  InfiniteScrollProps,
  MessageRole,
} from 'v2/models';
import { BeatLoader } from 'react-spinners';
import MarkdownRender from 'components/MarkdownRender/MarkdownRender';
import AIErrorMessage from './AIErrorMessage';
import InfiniteScrollV2 from 'v2/common/InfiniteScrollV2/InfiniteScrollV2';
import AILimitMessage from './AILimitMessage';
import {
  CurrentStateWaitingConversationContext,
  isAIMessageLimitReached,
} from 'v2/utils/AISearchUtils';
import { REACHED_LIMIT_MESSAGE, WAITING_MESSAGE } from 'utils/constants';

const WaitingMessage = () => (
  <div className={classnames(styles.infoMessage)}>
    <p>Please wait while we process your search query.</p>
    <div>
      <BeatLoader color="#29384D" loading size={10} />
    </div>
  </div>
);

const InitialMessage = () => (
  <div className={classnames(styles.infoMessage)}>
    <p>Please type a question below to get started.</p>
  </div>
);

type MessageProps = {
  role: MessageRole;
  text: string;
  file: string | null;
};

const Message = ({ role, text, file }: MessageProps) => {
  text = text.trim();
  let lines = text.split('\n');
  lines = lines.map((line) =>
    line.replace(/^\s+/, (match) => '\u00A0'.repeat(match.length))
  );
  text = lines.join('\n');

  if (text.startsWith('```markdown') && text.endsWith('```')) {
    text = text.replace(/^```markdown/, '');
    text = text.replace(/```$/, '');
  }

  if (text.includes('/api/v1/ai_conversation_message/') && file) {
    const regex =
      /\/api\/v1\/ai_conversation_message\/(\d+)\/download_file\.([a-zA-Z0-9.-]+)/g;
    text = text.replace(regex, file);
  }

  return (
    <div
      className={classnames(
        styles.chatMessageContainer,
        role === MessageRole.USER
          ? styles.userMessageContainer
          : styles.customBackground
      )}
      role="log"
    >
      <MarkdownRender
        className={classnames(styles.markdownMessage)}
        source={text}
        format={true}
      />
    </div>
  );
};

interface OwnProps {
  messages: AIConversationMessage[];
  aiConversation: AIConversation;
}

type AIChatProps = OwnProps & InfiniteScrollProps;

const AIChat = ({
  messages,
  aiConversation,
  fetchNextPage,
  hasNextPage,
}: AIChatProps) => {
  const chatRef = useRef<HTMLDivElement>(null);
  const [limitMessage, setLimitMessage] = useState<string | null>(null);
  const { waitingConversation } = useContext(
    CurrentStateWaitingConversationContext
  );

  useEffect(() => {
    if (chatRef.current && chatRef.current.scrollTo)
      chatRef.current.scrollTo(0, chatRef.current.scrollHeight);
  }, [messages[messages.length - 1]?.id, aiConversation.status]);

  useEffect(() => {
    if (isAIMessageLimitReached(messages)) {
      setLimitMessage(REACHED_LIMIT_MESSAGE);
    } else if (
      Number.isInteger(waitingConversation) &&
      waitingConversation !== aiConversation.id
    ) {
      setLimitMessage(WAITING_MESSAGE);
    } else {
      setLimitMessage(null);
    }
  }, [messages, waitingConversation]);

  if (messages.length === 0) {
    return (
      <div className={classnames(styles.chatWindow)}>
        {Number.isInteger(waitingConversation) ? (
          <AILimitMessage message={WAITING_MESSAGE} />
        ) : (
          <InitialMessage />
        )}
      </div>
    );
  }
  const isWaiting = [
    AIConversationStatus.QUEUED,
    AIConversationStatus.IN_PROCESS,
  ].includes(aiConversation.status);
  const isFailed = aiConversation.status === AIConversationStatus.FAILED;

  return (
    <div ref={chatRef} className={classnames(styles.chatWindow)}>
      <InfiniteScrollV2
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        rootMargin={'3000px 0px'}
        root={chatRef.current}
      />
      {messages.map(({ id, role, text, file }) => (
        <Message key={id} role={role} text={text} file={file} />
      ))}
      {isFailed && <AIErrorMessage aiConversation={aiConversation} />}
      {isWaiting && <WaitingMessage />}
      {limitMessage && <AILimitMessage message={limitMessage} />}
    </div>
  );
};

export default AIChat;
